/**
 * LOS DRAFTS, CONVERTIDOS EN ÁRBOLES DIBUJABLES.
 *
 * Los drafts de la consola llegan como LISTAS de nodos, cada uno con el nombre
 * de su padre. Eso se audita bien en una tabla y se lee fatal como forma: nadie
 * ve en una columna que una pierna entera cuelga de quien no debe. Aquí se
 * rehace la relación padre→hijos para poder pintarla.
 *
 * SON TRES ÁRBOLES DISTINTOS Y ÉSE ES EL PUNTO:
 *
 *   · BINARIO (`binaryParentPK`) — dónde queda colocado cada uno. La posición,
 *     que tiene consecuencias de dinero y no se deshace. Se cuelga por DERRAME:
 *     uno acaba debajo de cualquiera que tuviera la pierna libre.
 *   · PATROCINIO / unilevel (`unilevelParentPK`) — de quién es cada uno, que es
 *     quien cobra por él. NO coincide con el binario: `maph01` lo patrocina
 *     `pitgtr` y cuelga en el binario de `monicamenmal`.
 *   · CMMI (`parentPK`) — la tercera red, de un solo vínculo y con PASES: un
 *     nodo con `isPassUp` no cuelga de quien lo patrocinó sino de alguien más
 *     arriba, porque el patrocinador no calificaba.
 *
 * TODO SALE EN LA MISMA FORMA (`DraftTreeItem`) Y CON LA MISMA TARJETA
 * (`DraftTreeCard`), y por eso hay UN componente de rama y no tres casi
 * iguales. Lo que cambia entre árboles —qué campo hace de padre, qué dice cada
 * renglón, qué marcas lleva— se decide aquí, en una transformación pura; el
 * componente sólo pinta lo que le llega.
 *
 * ESTO NO SABE NADA DE VUE NI DE FIRESTORE: entra el draft que ya está en
 * memoria en el cliente (no se vuelve a llamar al backend) y sale un árbol. Así
 * se puede probar con un draft de mentira sin montar un componente.
 *
 * NADA SE PIERDE POR EL CAMINO, que es la regla de la que depende que revisar
 * esto sirva de algo. Un nodo que no cuelga de la raíz no desaparece: sale como
 * `detached`. Uno que no está en el binario sale como `offTree`. Y dos nodos en
 * la MISMA pierna del MISMO padre —que no puede pasar— salen en `duplicateLegs`
 * en vez de dibujarse uno encima de otro.
 */

import { normalizePackageLabel } from "functions/src/Subscriptions/packageIds";
import {
  MigrationDraft,
  MigrationDraftNode,
  MigrationMmiDraft,
  MigrationMmiDraftNode,
} from "./types";

/** Qué árbol se está mirando. */
export type DraftTreeMode = "binary" | "unilevel" | "cmmi";

export type DraftSide = "left" | "right" | null;

/**
 * EL TERCER RENGLÓN DE LA TARJETA: un dato que se compara con otro.
 *
 * En el binario es el patrocinador («de: pitgtr»), porque ahí se cuelga por
 * derrame y sin esto no se sabe de quién es nadie. En el CMMI es dónde colgaba
 * antes («antes: monicamenmal»), que es la comparación que hace entender un
 * pase. El prefijo va aparte del valor para poder apagarlo en gris: el ojo
 * tiene que caer en el NOMBRE, no en la etiqueta.
 */
export interface DraftTreeDetail {
  prefix: string;
  value: string;
  /** Marca en rojo: hay algo que mirar, no es el caso normal. */
  warn?: boolean;
  /** Qué se explica al pasar el ratón por encima de este renglón. */
  title?: string;
}

/**
 * LO QUE SE PINTA EN UNA TARJETA, ya resuelto.
 *
 * El componente de rama no sabe de qué árbol viene ni qué campos tenía el nodo:
 * recibe esto. Toda la traducción —el nombre comercial del paquete, quién es el
 * patrocinador, qué significa el pase— se hace UNA vez aquí, y así el dibujo de
 * la pantalla y el del fichero exportado no pueden decir cosas distintas.
 */
export interface DraftTreeCard {
  /** El nombre, en grande. */
  name: string;
  /** Segundo renglón: el paquete comercial, o la lista del CMMI. */
  sub: string;
  /** Tercer renglón, cuando hay algo que comparar. */
  detail?: DraftTreeDetail;
  /** Ancla: la colocación se fijó a mano, no la decidió el sistema. */
  isAnchor?: boolean;
  /** El ápice, que no es un miembro del draft. */
  isRoot?: boolean;
  /** CMMI: subió por encima de su patrocinador (pase). */
  isPassUp?: boolean;
  /** Todo el detalle del nodo, para el tooltip. */
  tooltip: string;
}

export interface DraftTreeItem {
  username: string;
  card: DraftTreeCard;
  /**
   * LA PIERNA, Y SIGNIFICA UNA COSA DISTINTA EN CADA ÁRBOL — el mismo campo
   * porque se pinta igual, no porque sea el mismo dato.
   *
   *  · En binario es `binarySide`: DÓNDE cuelga físicamente. Es la posición, y
   *    la posición no se deshace.
   *  · En unilevel es `unilevelSide`: a qué pierna de su patrocinador le SUMAN
   *    los puntos de rango. Ahí no decide la forma del dibujo (un patrocinador
   *    tiene los directos que tenga, no dos), pero es lo que decide el dinero:
   *    el roll-up de rango sube por `unilevelTree.parent` + `unilevelSide`
   *    (`PointsFunc.ts`), no por el lado del binario.
   *  · En CMMI no hay lados: siempre `null`.
   *
   * `null` también en el ápice, que no es un nodo del draft.
   */
  side: DraftSide;
  children: DraftTreeItem[];
  /** Cuántos nodos cuelgan por debajo (sin contarse él). */
  descendants: number;
  depth: number;
}

/** Un nodo que este árbol NO dibuja pero que existe: se nombra, no se traga. */
export interface DraftOffTreeEntry {
  username: string;
  label: string;
}

export interface DraftForest {
  mode: DraftTreeMode;
  /** El ápice (`draft.root`) con todo lo que cuelga de él. */
  root: DraftTreeItem;
  /**
   * Los que NO cuelgan del ápice: su padre no está en el draft, o la cadena da
   * una vuelta sobre sí misma. Se dibujan aparte para que se vean, nunca se
   * descartan.
   */
  detached: DraftTreeItem[];
  /** Sólo en binario: los que no ocupan hueco (`place: "unilevel"`). */
  offTree: DraftOffTreeEntry[];
  /** `padre (lado)` con más de un nodo — el draft está corrupto si sale algo. */
  duplicateLegs: string[];
  /** Nodos del draft que este árbol dibuja. */
  drawn: number;
  /** Nodos del draft, en total. */
  total: number;
  maxDepth: number;
  /** Sólo en CMMI: cuántos subieron por encima de su patrocinador. */
  passUps: number;
}

/**
 * EL NOMBRE COMERCIAL DEL PAQUETE, que es el único que reconoce quien aprueba.
 *
 * `full`/`lite`/`vip` (el `tier` del nodo) es vocabulario INTERNO del motor de
 * colocación y no significa nada fuera de él. La traducción de etiquetas viejas
 * a las vivas la hace `normalizePackageLabel`, que es la única puerta del
 * sistema por la que entran (ver la cabecera de `packageIds.ts`): un ancla
 * tecleada a mano puede traer todavía una etiqueta retirada.
 */
const PRETTY_PACKAGE: Record<string, string> = {
  "gtr-go": "GTR Go",
  "gtr-fly": "GTR Fly",
  "gtr-win": "GTR Win",
  none: "sin paquete",
};

export function packageLabel(pkg: string | undefined | null): string {
  if (!pkg) return "sin paquete";
  let live: string | undefined;
  try {
    live = normalizePackageLabel(pkg);
  } catch {
    // `normalizePackageLabel` se planta cuando le pasan un doc-id de suscripción
    // donde iba un nivel. Aquí eso no puede tumbar la revisión del árbol: se
    // pinta la cadena cruda, que es justo lo que hay que ver para arreglarla.
    live = undefined;
  }
  return PRETTY_PACKAGE[live ?? ""] ?? pkg;
}

/** Las listas del CMMI, con el nombre que usa la gente. */
const PRETTY_LIST: Record<string, string> = {
  prelaunch: "prelanzamiento",
  start: "arranque",
  cicle: "ciclo",
};

function normalizeSide(raw: string | undefined | null): DraftSide {
  if (raw === "left") return "left";
  if (raw === "right") return "right";
  return null;
}

function sideName(side: DraftSide | string | null): string {
  return side === "left" ? "izquierda" : side === "right" ? "derecha" : "—";
}

/**
 * LA PIERNA DEL NODO EN EL ÁRBOL QUE SE ESTÁ MIRANDO — dos campos distintos.
 *
 * `binarySide` es la posición; `unilevelSide` es la pierna del patrocinador a la
 * que le suman los puntos. El backend SIEMPRE rellena el segundo: para un ancla
 * sale del JSON de anclas y para todos los demás cae al lado binario
 * (`resolveDraft.ts:618`), y `parseDraft` lo exige obligatorio
 * (`draftIo.ts:181`), así que aquí nunca hay que inventárselo.
 */
function sideOf(node: MigrationDraftNode, mode: DraftTreeMode): DraftSide {
  return normalizeSide(mode === "binary" ? node.binarySide : node.unilevelSide);
}

/**
 * DE QUIÉN ES ESTA PERSONA — el dato que el árbol binario no enseñaba.
 *
 * En esta red las dos cosas se separan a propósito: en el binario la gente
 * cuelga POR DERRAME de cualquiera que tenga la pierna libre, pero el dinero lo
 * cobra su patrocinador. Mirando sólo el binario no hay forma de saber de quién
 * es cada nodo, y había que cambiar de pestaña para averiguarlo.
 *
 * SE PINTA `unilevelParentPK`, NO `enroller`, y la diferencia importa: el
 * primero es de quien cuelga en el árbol de patrocinio —lo que se ve en la otra
 * pestaña— y el segundo es quien le vendió. Para un nodo automático son la misma
 * persona (`resolveDraft` hace `unilevelParentPK = enroller`); un ANCLA puede
 * traer los dos distintos desde el JSON de anclas, y ahí el que manda para el
 * árbol es el padre. Cuando divergen se marca, porque un ancla sembrada con el
 * padre cambiado y el vendedor viejo es justo lo que hay que cazar aquí.
 */
export interface DraftSponsor {
  /** De quién cuelga en el árbol de patrocinio. */
  parent: string;
  /** Quien le vendió. */
  enroller: string;
  /** `true` cuando el vendedor NO es el padre de patrocinio. */
  diverges: boolean;
}

export function sponsorOf(
  node: MigrationDraftNode | null | undefined
): DraftSponsor | null {
  if (!node) return null;
  const parent = node.unilevelParentPK || "";
  const enroller = node.enroller || "";
  if (!parent && !enroller) return null;
  return {
    parent: parent || enroller,
    enroller,
    diverges: !!parent && !!enroller && parent !== enroller,
  };
}

// -- las tarjetas -----------------------------------------------------------

function rootCard(username: string): DraftTreeCard {
  return {
    name: username,
    sub: "raíz del árbol",
    isRoot: true,
    tooltip: `${username} — raíz del árbol`,
  };
}

function draftCard(
  node: MigrationDraftNode,
  mode: DraftTreeMode
): DraftTreeCard {
  const spon = sponsorOf(node);
  const lado = node.binarySide ? ` (${sideName(node.binarySide)})` : "";
  const lineas = [
    node.displayUsername || node.username,
    `paquete: ${packageLabel(node.package)}`,
    `patrocinador: ${node.enroller || "—"}`,
    `padre binario: ${node.binaryParentPK || "— (fuera del binario)"}${lado}`,
    // El lado unilevel va SIEMPRE, aunque coincida con el binario: es la pierna
    // por la que le suben los puntos de rango a su patrocinador, y coincidir es
    // el caso normal, no el motivo para callarlo.
    `padre unilevel: ${node.unilevelParentPK || "—"}${
      node.unilevelSide ? ` (${sideName(node.unilevelSide)})` : ""
    }`,
    `rango: ${node.rankId || "—"} · orden de colocación: ${node.order}`,
  ];
  if (node.isAnchor) lineas.push("ANCLA — viene del JSON de anclas");

  return {
    name: node.displayUsername || node.username,
    sub: packageLabel(node.package),
    // EL TERCER RENGLÓN, SÓLO EN EL BINARIO. En el árbol de patrocinio no se
    // repite: allí el padre YA es esta misma persona.
    detail:
      mode === "binary" && spon
        ? {
            prefix: "de:",
            value: spon.parent,
            warn: spon.diverges,
            title: spon.diverges
              ? `cuelga en unilevel de "${spon.parent}", pero quien le vendió ` +
                `es "${spon.enroller}" — un ancla con el padre cambiado`
              : `patrocinador (de quién cuelga en unilevel): ${spon.parent}`,
          }
        : undefined,
    isAnchor: !!node.isAnchor,
    tooltip: lineas.join("\n"),
  };
}

/**
 * LA TARJETA DEL CMMI, y el dato que la justifica es el PASE.
 *
 * Un `isPassUp` significa que esa persona NO cuelga de quien la patrocinó: su
 * patrocinador no calificaba para recibirla y subió a alguien de más arriba de
 * la cadena. En la tabla eso es una columna de «sí/no» que no explica nada;
 * aquí el nodo se marca y se dice de dónde viene.
 *
 * ⚠️ EL TERCER RENGLÓN ES EL PADRE VIEJO, NO «de quién habría colgado sin el
 * pase». Ese segundo dato NO está en el draft del CMMI: sus nodos no llevan el
 * enroller (`mmiTypes.ts`), sólo el padre nuevo y el legacy. Se pinta lo que
 * hay —dónde colgaba antes— porque es la comparación real y comprobable; poner
 * ahí una suposición sería peor que no poner nada.
 */
function mmiCard(node: MigrationMmiDraftNode): DraftTreeCard {
  const movido = !!node.legacyParent && node.legacyParent !== node.parentPK;
  const lineas = [
    node.username,
    `cuelga de: ${node.parentPK}`,
    `antes colgaba de: ${
      node.legacyParent ?? "— (no tenía nodo en el CMMI viejo)"
    }`,
    `pase (pass-up): ${node.isPassUp ? "SÍ" : "no"}${
      node.legacyIsPassUp == null
        ? ""
        : ` · antes: ${node.legacyIsPassUp ? "sí" : "no"}`
    }`,
    `lista del padre: ${
      node.listId ? PRETTY_LIST[node.listId] ?? node.listId : "— (génesis)"
    }`,
    `fecha del CMMI: ${
      node.cmmiDateMs ? new Date(node.cmmiDateMs).toLocaleDateString() : "—"
    } · orden de replay: ${node.order}`,
  ];
  if (node.isPassUp) {
    lineas.push(
      "PASE: no cuelga de quien lo patrocinó — su patrocinador no calificaba y subió a alguien de más arriba"
    );
  }

  return {
    name: node.username,
    sub: node.isGenesis
      ? "génesis"
      : `lista ${node.listId ? PRETTY_LIST[node.listId] ?? node.listId : "—"}`,
    detail: movido
      ? {
          prefix: "antes:",
          value: node.legacyParent as string,
          warn: true,
          title:
            `en el CMMI viejo colgaba de "${node.legacyParent}" y ahora ` +
            `cuelga de "${node.parentPK}"`,
        }
      : undefined,
    isPassUp: !!node.isPassUp,
    tooltip: lineas.join("\n"),
  };
}

// -- el armazón, compartido por los tres árboles ---------------------------

/** Lo que el armazón necesita saber de un nodo, sea del draft que sea. */
interface TreeInput {
  username: string;
  parent: string;
  side: DraftSide;
  order: number;
  card: DraftTreeCard;
  isPassUp: boolean;
}

/**
 * IZQUIERDA PRIMERO, SIEMPRE. El orden de los hermanos no es cosmético en el
 * binario: es la posición, y la posición es dinero. Dentro de la misma pierna
 * (que ya sería un error) manda el orden de colocación. En los otros dos árboles
 * no hay lados y manda el orden a secas.
 */
function sortInputs(list: TreeInput[]): TreeInput[] {
  return [...list].sort(
    (a, b) =>
      (a.side === "left" ? 0 : 1) - (b.side === "left" ? 0 : 1) ||
      a.order - b.order
  );
}

function assemble(
  mode: DraftTreeMode,
  rootPK: string,
  inputs: TreeInput[],
  offTree: DraftOffTreeEntry[],
  total: number
): DraftForest {
  const childrenOf = new Map<string, TreeInput[]>();
  for (const input of inputs) {
    const list = childrenOf.get(input.parent);
    if (list) list.push(input);
    else childrenOf.set(input.parent, [input]);
  }

  const duplicateLegs: string[] = [];
  if (mode === "binary") {
    childrenOf.forEach((list, parent) => {
      for (const side of ["left", "right"] as const) {
        if (list.filter((n) => n.side === side).length > 1) {
          duplicateLegs.push(`${parent} (${side})`);
        }
      }
    });
  }

  // `visited` hace de cortacircuitos: un nodo ya dibujado no se vuelve a
  // expandir, así que una cadena que se muerde la cola se corta en vez de
  // colgar el navegador con una recursión infinita.
  const visited = new Set<string>();
  let maxDepth = 0;

  const build = (input: TreeInput | null, username: string, depth: number) => {
    visited.add(username);
    if (depth > maxDepth) maxDepth = depth;
    const item: DraftTreeItem = {
      username,
      card: input ? input.card : rootCard(username),
      side: input ? input.side : null,
      children: [],
      descendants: 0,
      depth,
    };
    for (const child of sortInputs(childrenOf.get(username) ?? [])) {
      if (visited.has(child.username)) continue;
      const sub = build(child, child.username, depth + 1);
      item.children.push(sub);
      item.descendants += 1 + sub.descendants;
    }
    return item;
  };

  const byName = new Map(inputs.map((i) => [i.username, i]));

  // EL ÁPICE NO SE DIBUJA — no es una persona.
  //
  // `draft.root` (`rootuser`) es el terminador de las cadenas, no un miembro:
  // `resolveDraft` hace `members.delete(root)`, así que nunca tiene nodo, ni
  // contadores, ni espejo en `users.positioned`. Dibujarlo obligaba a
  // inventarle una tarjeta (`rootCard`) y ponía en pantalla una cuenta que no
  // existe, justo encima de la que sí es la cima real de la red.
  //
  // Así que el árbol arranca en lo que cuelga del ápice — la cuenta de empresa.
  // Si algún día colgara MÁS DE UNA cosa de la raíz, no habría un solo árbol
  // sino varios, y ahí el ápice sí es lo único que los une: en ese caso se
  // vuelve a dibujar, porque quitarlo escondería que están separados.
  const topInputs = sortInputs(childrenOf.get(rootPK) ?? []);
  const rootInput = byName.get(rootPK) ?? null;
  const root =
    !rootInput && topInputs.length === 1
      ? build(topInputs[0], topInputs[0].username, 0)
      : build(rootInput, rootPK, 0);

  const detached: DraftTreeItem[] = [];
  for (const input of [...inputs].sort((a, b) => a.order - b.order)) {
    if (visited.has(input.username)) continue;
    detached.push(build(input, input.username, 0));
  }

  return {
    mode,
    root,
    detached,
    offTree,
    duplicateLegs,
    drawn: inputs.filter((i) => visited.has(i.username)).length,
    total,
    maxDepth,
    passUps: inputs.filter((i) => i.isPassUp).length,
  };
}

/**
 * ¿Ocupa hueco en el binario? Un `place: "unilevel"` (los Lite, según la config)
 * no tiene padre binario y es TRANSPARENTE al derrame: no se dibuja en ese
 * árbol, pero tampoco se calla.
 */
function inBinary(node: MigrationDraftNode): boolean {
  return node.place !== "unilevel" && !!node.binaryParentPK;
}

export function buildDraftForest(
  draft: MigrationDraft,
  mode: "binary" | "unilevel"
): DraftForest {
  const nodes = draft.nodes ?? [];
  const rootPK = draft.root || "(sin raíz)";

  const offTree: DraftOffTreeEntry[] = [];
  const inputs: TreeInput[] = [];
  for (const node of nodes) {
    if (mode === "binary" && !inBinary(node)) {
      offTree.push({
        username: node.username,
        label: node.displayUsername || node.username,
      });
      continue;
    }
    inputs.push({
      username: node.username,
      parent:
        (mode === "binary" ? node.binaryParentPK : node.unilevelParentPK) || "",
      side: sideOf(node, mode),
      order: node.order,
      card: draftCard(node, mode),
      isPassUp: false,
    });
  }

  return assemble(mode, rootPK, inputs, offTree, nodes.length);
}

/**
 * EL ÁRBOL DEL CMMI, la tercera red.
 *
 * Un solo tipo de vínculo —sin izquierda ni derecha—, así que por forma se
 * parece al de patrocinio. Lo que lo hace distinto es el PASE, y por eso vale
 * la pena dibujarlo: en la tabla, un nodo que cuelga de su «abuelo» en vez de
 * su patrocinador es una columna que dice `sí`; en el árbol se ve el salto.
 */
export function buildMmiForest(draft: MigrationMmiDraft): DraftForest {
  const nodes = draft.nodes ?? [];
  const rootPK = draft.root || "(sin raíz)";
  const inputs: TreeInput[] = nodes.map((node) => ({
    username: node.username,
    parent: node.parentPK || "",
    side: null,
    order: node.order,
    card: mmiCard(node),
    isPassUp: !!node.isPassUp,
  }));
  return assemble("cmmi", rootPK, inputs, [], nodes.length);
}

/**
 * Los que pliega «colapsar todo». Las raíces se quedan fuera a propósito: si se
 * plegaran también, el botón dejaría la pantalla en blanco.
 */
export function collapsibleUsernames(forest: DraftForest): string[] {
  const out: string[] = [];
  const walk = (item: DraftTreeItem): void => {
    if (item.children.length && item.depth > 0) out.push(item.username);
    item.children.forEach(walk);
  };
  walk(forest.root);
  forest.detached.forEach(walk);
  return out;
}
